import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Table, Tag, Select, Button, Space, DatePicker, Card,
  Drawer, Descriptions, Spin, message, Divider, Form, List, Avatar,
  Input, Modal, Checkbox, Alert,
} from 'antd';
import {
  BellOutlined, UserOutlined, ReloadOutlined, RobotOutlined,
  SendOutlined, CheckCircleOutlined, StopOutlined, LinkOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import ComplianceCreateModal from '../components/ComplianceCreateModal';
import ErrorCreateModal from '../components/ErrorCreateModal';
import { usePageLabel } from '../components/BackChip';

const { RangePicker } = DatePicker;
const { TextArea }    = Input;

const SEVERITY_COLOR = { low: 'blue', medium: 'orange', high: 'red' };
const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

const STATUS_COLOR = { Open: 'blue', In_Progress: 'orange', Complete: 'green', Ignored: 'default' };
const STATUS_LABEL = { Open: 'Open', In_Progress: 'In Progress', Complete: 'Complete', Ignored: 'Ignored' };
const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l }));

const fmt    = (s) => s ?? '—';
const fmtSec = (n) => (n == null ? '—' : `${n}s`);


// ── Bulk-action modal ─────────────────────────────────────────────────────────
function BulkActionModal({ open, ids, action, onConfirm, onCancel }) {
  const [note, setNote] = useState('');
  const label = action === 'Ignored' ? 'Dismiss' : 'Complete';

  const handleOk = () => {
    onConfirm(ids, action, note.trim());
    setNote('');
  };

  return (
    <Modal maskClosable={false}
      open={open}
      title={`${label} ${ids.length} notification${ids.length !== 1 ? 's' : ''}`}
      onOk={handleOk}
      onCancel={() => { setNote(''); onCancel(); }}
      okText={label}
      okButtonProps={{ danger: action === 'Ignored' }}
    >
      <TextArea
        rows={3}
        placeholder="Optional note (saved as a comment on each entry)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{ marginTop: 8 }}
      />
    </Modal>
  );
}



// ── Detail drawer ─────────────────────────────────────────────────────────────
function NotificationDrawer({ id, onClose, onUpdated, onSelectId }) {
  const currentUser = useContext(UserContext);
  const navigate    = useNavigate();
  const canEdit     = (currentUser?.CanEditErrors ?? 0) >= 2 || (currentUser?.CanEditIncidents ?? 0) >= 2;

  const [notif,           setNotif]           = useState(null);
  const [users,           setUsers]           = useState([]);
  const [campaigns,       setCampaigns]       = useState([]);
  const [comments,        setComments]        = useState([]);
  const [newComment,      setNewComment]      = useState('');
  const [loading,         setLoading]         = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [posting,         setPosting]         = useState(false);
  const [relSelected,     setRelSelected]     = useState([]);
  const [bulkModal,          setBulkModal]          = useState(null); // { ids, action }
  const [createErrOpen,      setCreateErrOpen]      = useState(false);
  const [complianceOpen,     setComplianceOpen]     = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setRelSelected([]);
    try {
      const [nr, ur, cr, campsR] = await Promise.all([
        api.get(`/api/notifications/${id}`),
        api.get('/api/users', { params: { can_edit_errors_or_incidents: 1 } }),
        api.get(`/api/notifications/${id}/comments`),
        api.get('/api/user/campaigns'),
      ]);
      setNotif(nr.data);
      setUsers(ur.data);
      setComments(cr.data);
      setCampaigns(campsR.data);
    } catch {
      message.error('Failed to load notification.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) { setNotif(null); setComments([]); setNewComment(''); setRelSelected([]); return; }
    load();
  }, [load, id]);

  const submitComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post(`/api/notifications/${id}/comments`, { comment: text });
      setNewComment('');
      const cr = await api.get(`/api/notifications/${id}/comments`);
      setComments(cr.data);
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setPosting(false);
    }
  };

  const update = async (endpoint, body) => {
    setSaving(true);
    try {
      await api.put(`/api/notifications/${id}/${endpoint}`, body);
      await load();
      onUpdated?.();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleErrorCreated = async (errorId) => {
    setCreateErrOpen(false);
    try {
      await api.post(`/api/notifications/${id}/comments`, {
        comment: `Error log #${errorId} created from this notification.`,
      });
    } catch {}
    navigate(`/errors/${errorId}`);
  };

  const handleBulkAction = async (ids, status, comment) => {
    setSaving(true);
    setBulkModal(null);
    try {
      await api.put('/api/notifications/bulk-status', { ids, status, comment });
      message.success(`${ids.length} notification(s) updated.`);
      await load();
      onUpdated?.();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Bulk action failed.');
    } finally {
      setSaving(false);
    }
  };

  const callCols = [
    {
      title: 'Contact ID', dataIndex: 'ContactID', width: 120,
      render: (v) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/calls/${v}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Metric', dataIndex: 'MetricLabel', width: 160,
      sorter: (a, b) => (a.MetricLabel ?? '').localeCompare(b.MetricLabel ?? ''),
      defaultSortOrder: 'ascend',
      render: (v) => v ? <Tag color="purple" style={{ fontSize: 11 }}>{v}</Tag> : null,
    },
    { title: 'Date',       dataIndex: 'Date',             width: 100 },
    { title: 'Time',       dataIndex: 'Time',             width: 80  },
    { title: 'Agent',      dataIndex: 'Agent_Name',       ellipsis: true },
    { title: 'Campaign',   dataIndex: 'Campaign',         width: 110 },
    { title: 'Skill',      dataIndex: 'Skill',            ellipsis: true },
    { title: 'Duration',   dataIndex: 'Agent_Time',       width: 85, render: fmtSec },
    { title: 'ACW',        dataIndex: 'ACW_Time',         width: 70, render: fmtSec },
    { title: 'Disposition',dataIndex: 'Disp_Name',        ellipsis: true },
    { title: 'Direction',  dataIndex: 'Direction',        width: 90 },
    { title: 'Media',      dataIndex: 'Media_Name',       width: 90 },
    { title: 'End Reason', dataIndex: 'ContactEndReason', ellipsis: true },
  ];

  const relCols = [
    {
      title: '', key: 'check', width: 36,
      render: (_, r) => (
        <Checkbox
          checked={relSelected.includes(r.ID)}
          onChange={(e) => setRelSelected((prev) =>
            e.target.checked ? [...prev, r.ID] : prev.filter((x) => x !== r.ID)
          )}
        />
      ),
    },
    { title: 'Date',     dataIndex: 'CreateDate',    width: 100 },
    { title: 'Severity', dataIndex: 'Severity',      width: 85,
      render: (v) => v ? <Tag color={SEVERITY_COLOR[v]}>{v}</Tag> : null },
    { title: 'Summary',  dataIndex: 'Summary',       ellipsis: true },
    { title: 'Campaign', dataIndex: 'Campaign_Name', width: 110 },
    { title: 'Status',   dataIndex: 'Status',        width: 110,
      render: (v) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v] ?? v}</Tag> },
    { title: 'Assigned', dataIndex: 'AssignedToName', width: 130, ellipsis: true,
      render: (v) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
  ];

  const openRelIds = notif?.RelatedNotifications?.filter((r) => r.Status === 'Open').map((r) => r.ID) ?? [];

  return (
    <Drawer
      title={
        notif ? (
          <Space>
            <Tag color={SEVERITY_COLOR[notif.Severity]}>{notif.Severity ?? '—'}</Tag>
            <span>{notif.Type}</span>
            {notif.Agent_Name && <Typography.Text type="secondary">— {notif.Agent_Name}</Typography.Text>}
          </Space>
        ) : 'Loading…'
      }
      open={!!id}
      onClose={onClose}
      width={1100}
      destroyOnClose
    >
      {loading && <div style={{ textAlign: 'center', marginTop: 60 }}><Spin size="large" /></div>}

      {!loading && notif && (
        <>
          {/* ── Header ── */}
          <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Agent" span={2}>
              {notif.AgentUserID && (currentUser?.CanViewAgentSummary ?? 0) >= 1
                ? <Link to={`/agents?user_id=${notif.AgentUserID}`}>{fmt(notif.Agent_Name)} {notif.Agent_No ? `(${notif.Agent_No})` : ''}</Link>
                : <span>{fmt(notif.Agent_Name)} {notif.Agent_No ? `(${notif.Agent_No})` : ''}</span>
              }
            </Descriptions.Item>
            <Descriptions.Item label="Created">{notif.CreateDate}</Descriptions.Item>
            <Descriptions.Item label="Team">{fmt(notif.TeamName)}</Descriptions.Item>
            <Descriptions.Item label="Campaign">{fmt(notif.Campaign_Name)}</Descriptions.Item>
            <Descriptions.Item label="Date Range">
              {notif.DateFrom ? `${notif.DateFrom} → ${notif.DateTo}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Call Count">{fmt(notif.CallCount)}</Descriptions.Item>
          </Descriptions>

          {/* ── Actions ── */}
          <Space wrap style={{ marginBottom: 16 }}>
            <Space>
              <Typography.Text strong>Status:</Typography.Text>
              <Select
                value={notif.Status}
                options={STATUS_OPTIONS}
                onChange={canEdit ? (v) => update('status', { status: v }) : undefined}
                disabled={!canEdit}
                loading={saving}
                style={{ width: 140 }}
              />
            </Space>
            <Space>
              <Typography.Text strong>Assigned to:</Typography.Text>
              <Select
                value={notif.AssignedToUserID ?? undefined}
                placeholder="Unassigned"
                showSearch
                optionFilterProp="label"
                allowClear
                options={users.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))}
                onChange={canEdit ? (v) => update('assign', { user_id: v ?? null }) : undefined}
                disabled={!canEdit}
                loading={saving}
                style={{ width: 200 }}
              />
            </Space>
            {((currentUser?.CanEditErrors ?? 0) >= 2 || (currentUser?.CanEditIncidents ?? 0) >= 2) && notif.Status !== 'Complete' && (() => {
              const opts = [...campaigns];
              if (notif.Campaign_Name && !opts.includes(notif.Campaign_Name)) {
                opts.unshift(notif.Campaign_Name);
              }
              return (
                <Space>
                  <Typography.Text strong>Campaign:</Typography.Text>
                  <Select
                    value={notif.Campaign_Name ?? undefined}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    placeholder="None"
                    options={opts.map((c) => ({ label: c, value: c }))}
                    onChange={(v) => update('campaign', { campaign: v ?? null })}
                    loading={saving}
                    style={{ width: 200 }}
                  />
                </Space>
              );
            })()}
            {canEdit && !notif.AssignedToUserID && (
              <Button
                size="small"
                icon={<UserOutlined />}
                onClick={() => update('assign', { user_id: currentUser?.ID })}
              >
                Assign to me
              </Button>
            )}
            {(currentUser?.CanEditErrors ?? 0) >= 2 && (
              <Button
                size="small"
                icon={<FileAddOutlined />}
                onClick={() => setCreateErrOpen(true)}
              >
                Create Error Log
              </Button>
            )}
            {(currentUser?.CanEditIncidents ?? 0) >= 2 && notif.AgentUserID && (
              <Button
                size="small"
                icon={<FileAddOutlined />}
                onClick={() => setComplianceOpen(true)}
              >
                Create Compliance Incident
              </Button>
            )}
          </Space>

          <Divider style={{ margin: '12px 0' }} />

          {/* ── AI Assessment ── */}
          {(notif.Summary || notif.AIAssessment) && (
            <Card
              size="small"
              title={<Space><RobotOutlined style={{ color: '#722ed1' }} /><span>AI Assessment</span></Space>}
              style={{ marginBottom: 16, background: '#fafafa', borderColor: '#d3adf7' }}
            >
              {notif.Summary && (
                <Typography.Text strong style={{ display: 'block', marginBottom: notif.AIAssessment ? 8 : 0 }}>
                  {notif.Summary}
                </Typography.Text>
              )}
              {notif.AIAssessment && (
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0, color: '#595959' }}>
                  {notif.AIAssessment}
                </Typography.Paragraph>
              )}
            </Card>
          )}

          {/* ── Related notifications (same agent + type) ── */}
          {notif.RelatedNotifications?.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Typography.Text strong>
                  Other entries — same agent &amp; type&nbsp;
                  <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                    ({notif.RelatedNotifications.length} in last 180 days)
                  </Typography.Text>
                </Typography.Text>
                <Space>
                  {canEdit && relSelected.length > 0 && (
                    <>
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        onClick={() => setBulkModal({ ids: relSelected, action: 'Ignored' })}
                      >
                        Dismiss selected ({relSelected.length})
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => setBulkModal({ ids: relSelected, action: 'Complete' })}
                      >
                        Complete selected ({relSelected.length})
                      </Button>
                    </>
                  )}
                  {canEdit && openRelIds.length > 0 && relSelected.length === 0 && (
                    <>
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        onClick={() => setBulkModal({ ids: openRelIds, action: 'Ignored' })}
                      >
                        Dismiss all open ({openRelIds.length})
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => setBulkModal({ ids: openRelIds, action: 'Complete' })}
                      >
                        Complete all open ({openRelIds.length})
                      </Button>
                    </>
                  )}
                </Space>
              </div>
              <Table
                dataSource={notif.RelatedNotifications}
                rowKey="ID"
                columns={relCols}
                size="small"
                pagination={{ defaultPageSize: 10, size: 'small', hideOnSinglePage: true }}
                style={{ marginBottom: 16 }}
                onRow={(record) => ({
                  onClick: (e) => {
                    if (e.target.closest('.ant-checkbox-wrapper')) return;
                    onSelectId?.(record.ID);
                  },
                  style: { cursor: 'pointer' },
                })}
              />
            </>
          )}

          {/* ── Related calls ── */}
          {notif.Calls?.length > 0 && (
            <>
              <Typography.Text strong>Related Calls ({notif.Calls.length})</Typography.Text>
              <Table
                dataSource={notif.Calls}
                columns={callCols}
                rowKey="ContactID"
                size="small"
                scroll={{ x: 1000 }}
                style={{ marginTop: 8, marginBottom: 16 }}
                pagination={{ defaultPageSize: 10, size: 'small', hideOnSinglePage: true }}
              />
            </>
          )}

          <Divider style={{ margin: '12px 0' }} />

          {/* ── Comments ── */}
          <Typography.Text strong>Comments ({comments.length})</Typography.Text>
          <List
            style={{ marginTop: 8, marginBottom: 12, maxHeight: 320, overflowY: 'auto' }}
            dataSource={comments}
            locale={{ emptyText: 'No comments yet.' }}
            renderItem={(c) => {
              const isSystem = c.UserName === 'System';
              return (
                <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={isSystem ? <RobotOutlined /> : <UserOutlined />}
                        style={{ backgroundColor: isSystem ? '#722ed1' : '#1677ff', flexShrink: 0 }}
                      />
                    }
                    title={
                      <Space size={8}>
                        <Typography.Text strong>{c.UserName}</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {c.CommentDate} {c.CommentTime}
                        </Typography.Text>
                      </Space>
                    }
                    description={
                      <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{c.Comment}</Typography.Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              rows={2}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment… (Ctrl+Enter to post)"
              onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter') submitComment(); }}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={posting}
              onClick={submitComment}
              style={{ height: 'auto', alignSelf: 'stretch' }}
            >
              Post
            </Button>
          </Space.Compact>
        </>
      )}

      {bulkModal && (
        <BulkActionModal
          open
          ids={bulkModal.ids}
          action={bulkModal.action}
          onConfirm={handleBulkAction}
          onCancel={() => setBulkModal(null)}
        />
      )}

      <ErrorCreateModal
        open={createErrOpen}
        agentUserId={notif?.AgentUserID}
        agentName={notif?.Agent_Name}
        campaignNo={notif?.Campaign_No ?? null}
        initialCampaign={notif?.Campaign_Name}
        initialAssignedToId={currentUser?.ID}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCreateErrOpen(false)}
        onCreated={handleErrorCreated}
      />

      <ComplianceCreateModal
        open={complianceOpen}
        agentUserId={notif?.AgentUserID}
        agentName={notif?.Agent_Name}
        initialDate={notif?.DateFrom}
        initialDescription={notif?.Summary ? `AI Notification: ${notif.Summary}` : ''}
        initialTeamId={notif?.TeamID}
        currentUser={currentUser}
        navigate={navigate}
        sourceType="ai_notification"
        sourceId={notif?.ID ?? notif?.NotificationID}
        onClose={() => setComplianceOpen(false)}
        onCreated={async (newId) => {
          try {
            await api.post(`/api/notifications/${id}/comments`, {
              comment: `Compliance incident #${newId} created from this notification.`,
            });
          } catch {}
          message.success('Compliance incident created.');
        }}
        onCreatedAndOpen={async (newId) => {
          try {
            await api.post(`/api/notifications/${id}/comments`, {
              comment: `Compliance incident #${newId} created from this notification.`,
            });
          } catch {}
          navigate(`/compliance/incidents/${newId}`);
        }}
      />
    </Drawer>
  );
}


// ── Page ──────────────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const currentUser = useContext(UserContext);
  const canEdit     = (currentUser?.CanEditErrors ?? 0) >= 2 || (currentUser?.CanEditIncidents ?? 0) >= 2;
  const [form]      = Form.useForm();
  const { id: urlId } = useParams();
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(urlId ? `AI Notification #${urlId}` : null);

  const [rows,            setRows]            = useState([]);
  const [filterOpts,      setFilterOpts]      = useState({ types: [], campaigns: [], severities: [], statuses: [] });
  const [users,           setUsers]           = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [selectedId,      setSelectedId]      = useState(urlId ? Number(urlId) : null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filters,         setFilters]         = useState({ status: ['Open', 'In_Progress'], team: [] });
  const [bulkModal,       setBulkModal]       = useState(null);

  const defaultFilters = { status: ['Open', 'In_Progress'] };

  useEffect(() => {
    Promise.all([
      api.get('/api/notifications/filter-options'),
      api.get('/api/users', { params: { can_edit_errors_or_incidents: 1 } }),
    ]).then(([fo, ur]) => {
      setFilterOpts(fo.data);
      setUsers(ur.data);
    }).catch(() => {});
    fetchRows(defaultFilters);
    form.setFieldsValue({ status: defaultFilters.status });
  }, []); // eslint-disable-line

  const fetchRows = useCallback(async (f = filters) => {
    setLoading(true);
    setSelectedRowKeys([]);
    const params = {};
    if (f.campaign?.length)  params.campaign    = f.campaign.join(',');
    if (f.status?.length)    params.status      = f.status.join(',');
    if (f.severity?.length)  params.severity    = f.severity.join(',');
    if (f.team?.length)      params.team        = f.team.join(',');
    if (f.type)              params.type        = f.type;
    if (f.assigned_to)       params.assigned_to = f.assigned_to;
    if (f.dateRange?.[0])    params.date_from   = f.dateRange[0].format('YYYY-MM-DD');
    if (f.dateRange?.[1])    params.date_to     = f.dateRange[1].format('YYYY-MM-DD');
    try {
      const res = await api.get('/api/notifications', { params });
      setRows(res.data);
    } catch {
      message.error('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line

  const handleSearch = () => {
    const v = form.getFieldsValue();
    const f = {
      campaign:    v.campaign,
      status:      v.status,
      severity:    v.severity,
      team:        v.team,
      type:        v.type,
      assigned_to: v.assigned_to,
      dateRange:   v.dateRange,
    };
    setFilters(f);
    fetchRows(f);
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ status: defaultFilters.status });
    setFilters(defaultFilters);
    fetchRows(defaultFilters);
  };

  const handleBulkAction = async (ids, status, comment) => {
    setBulkModal(null);
    try {
      await api.put('/api/notifications/bulk-status', { ids, status, comment });
      message.success(`${ids.length} notification(s) updated.`);
      fetchRows();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Bulk action failed.');
    }
  };

  const sel = (options) => ({
    mode: 'multiple', allowClear: true, showSearch: true, optionFilterProp: 'label',
    options: options.map((v) => typeof v === 'string' ? { label: v, value: v } : v),
    style: { minWidth: 140 },
  });

  const cols = [
    {
      title: 'Severity', dataIndex: 'Severity', width: 90,
      sorter: (a, b) => (SEVERITY_ORDER[a.Severity] ?? 9) - (SEVERITY_ORDER[b.Severity] ?? 9),
      render: (v) => v ? <Tag color={SEVERITY_COLOR[v]}>{v}</Tag> : <Tag>—</Tag>,
    },
    {
      title: 'Type', dataIndex: 'Type', ellipsis: true, width: 180,
      render: (v) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Agent', dataIndex: 'Agent_Name', width: 160, ellipsis: true,
      render: (v, r) => (
        <Space size={4}>
          <span>{v ?? '—'}</span>
          {r.AgentUserID && (currentUser?.CanViewAgentSummary ?? 0) >= 1 && (
            <LinkOutlined
              style={{ color: '#1677ff', fontSize: 11 }}
              onClick={(e) => { e.stopPropagation(); window.open(`/agents?user_id=${r.AgentUserID}`, '_blank'); }}
            />
          )}
        </Space>
      ),
    },
    { title: 'Team',     dataIndex: 'TeamName',      width: 120, ellipsis: true, render: (v) => v ?? '—' },
    { title: 'Campaign', dataIndex: 'Campaign_Name', width: 110, ellipsis: true },
    { title: 'Summary',  dataIndex: 'Summary',       ellipsis: true },
    { title: 'Created',  dataIndex: 'CreateDate',    width: 100, sorter: (a, b) => a.CreateDate?.localeCompare(b.CreateDate) },
    {
      title: 'Status', dataIndex: 'Status', width: 110,
      render: (v) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v] ?? v}</Tag>,
    },
    {
      title: 'Assigned', dataIndex: 'AssignedToName', width: 140, ellipsis: true,
      render: (v) => v ?? <Typography.Text type="secondary">Unassigned</Typography.Text>,
    },
  ];

  const openSelected = selectedRowKeys.filter((k) => {
    const row = rows.find((r) => r.ID === k);
    return row?.Status === 'Open';
  });

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        <BellOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        AI Notifications
      </Typography.Title>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: 4 }}>
          <Form.Item name="type" label="Type">
            <Select
              placeholder="All" allowClear showSearch optionFilterProp="label"
              style={{ minWidth: 160 }}
              options={filterOpts.types.map((v) => ({ label: v, value: v }))}
            />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select placeholder="All" {...sel(filterOpts.severities)} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select placeholder="All" {...sel(STATUS_OPTIONS)} />
          </Form.Item>
          <Form.Item name="campaign" label="Campaign">
            <Select placeholder="All" {...sel(filterOpts.campaigns)} />
          </Form.Item>
          <Form.Item name="team" label="Team">
            <Select placeholder="All" {...sel(filterOpts.teams ?? [])} />
          </Form.Item>
          <Form.Item name="assigned_to" label="Assigned To">
            <Select
              placeholder="Anyone" allowClear showSearch optionFilterProp="label"
              style={{ minWidth: 160 }}
              options={[
                { label: 'Unassigned', value: 'unassigned' },
                { label: 'Me',         value: 'me' },
                ...users.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID })),
              ]}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="Created">
            <RangePicker style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch} loading={loading}>Search</Button>
              <Button onClick={handleReset}>Reset</Button>
              <Button icon={<ReloadOutlined />} onClick={() => fetchRows()} title="Refresh" />
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {selectedRowKeys.length > 0 && (
        <Alert
          style={{ marginBottom: 8 }}
          type="info"
          message={
            <Space>
              <span>{selectedRowKeys.length} selected</span>
              {canEdit && (
                <Button
                  size="small"
                  icon={<UserOutlined />}
                  onClick={async () => {
                    await api.put('/api/notifications/bulk-assign', { ids: selectedRowKeys });
                    message.success('Assigned to you.');
                    fetchRows();
                  }}
                >
                  Assign to Me
                </Button>
              )}
              {canEdit && openSelected.length > 0 && (
                <>
                  <Button
                    size="small"
                    icon={<StopOutlined />}
                    onClick={() => setBulkModal({ ids: openSelected, action: 'Ignored' })}
                  >
                    Dismiss open ({openSelected.length})
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => setBulkModal({ ids: openSelected, action: 'Complete' })}
                  >
                    Complete open ({openSelected.length})
                  </Button>
                </>
              )}
              <Button size="small" onClick={() => setSelectedRowKeys([])}>Clear</Button>
            </Space>
          }
        />
      )}

      <Table
        dataSource={rows}
        columns={cols}
        rowKey="ID"
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} notifications` }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e.target.closest('.ant-checkbox-wrapper')) return;
            setSelectedId(record.ID);
          },
          style: { cursor: 'pointer' },
        })}
      />

      <NotificationDrawer
        id={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={() => fetchRows()}
        onSelectId={(newId) => setSelectedId(newId)}
      />

      {bulkModal && (
        <BulkActionModal
          open
          ids={bulkModal.ids}
          action={bulkModal.action}
          onConfirm={handleBulkAction}
          onCancel={() => setBulkModal(null)}
        />
      )}
    </>
  );
}
